import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import AddIcon from "@material-ui/icons/Add";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button } from "@material-ui/core";

import ActiveProgramDictionary from "stores/dictionary/ActiveProgramDictionary";
import TodayNutritionStore from "stores/TodayNutritionStore";
import TodayQuickLinksStore from "stores/TodayQuickLinksStore";

import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";

import Page from "components/Layout/Page";
import ConfirmationDialog from "components/ConfirmationDialog";
import Placeholder from "components/Placeholder";
import PublishButton from "components/PublishButton";
import { SNACKBAR_TYPES } from "components/Snackbar";

import AccessForm from "../AccessForm";
import AddQuickLinksForm from "../AddQuickLinksForm";

import CategoryDetailsCard from "./CategoryDetailsCard";

import { PROGRAM_PERMISSION_TYPES, PUBLISH_STATUSES } from "constants/models";

import ROUTES from "navigation/routes";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { areStatusActiveProgramFiltersEmpty } from "utils/filter";
import { getConfirmRemovalText } from "utils/removeItem";

import {
  CategoryCardWrapper,
  BackButtonWrapper,
  SearchContainer,
  Search,
  ScrollLoader,
  OverflowLoader,
} from "./styles";

type Props = GlobalPopupProps &
  SnackbarContextProps &
  RouteComponentProps<{ id: string }> & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class TodayDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;

  get data() {
    const { pathname } = this.props.location;
    switch (pathname) {
      case ROUTES.IN_APP_TODAY_NUTRITION:
        return {
          store: TodayNutritionStore,
          detailsRoute: ROUTES.IN_APP_TODAY_NUTRITION_QUICK_LINK_DETAILS,
          pageTitle: "Nutrition",
        };
      case ROUTES.IN_APP_TODAY_QUICK_LINKS:
        return {
          store: TodayQuickLinksStore,
          detailsRoute: ROUTES.IN_APP_TODAY_QUICK_LINKS_LINK_DETAILS,
          pageTitle: "Quick Links",
        };
    }
  }

  get rootRoute() {
    const { pathname } = this.props.location;
    return pathname;
  }

  @computed get quickLinks() {
    return this.data?.store?.items ?? [];
  }

  @computed get quickLinksListIsEmpty() {
    return this.quickLinks.length === 0;
  }

  @computed get isFiltersEmpty() {
    const { filters } = this.data?.store ?? {};

    return areStatusActiveProgramFiltersEmpty(filters);
  }

  componentDidMount() {
    this.data?.store?.fetch();
    ActiveProgramDictionary.fetch();
  }

  componentWillUnmount() {
    this.data?.store?.reset();
  }

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  onBackClick = () => {
    this.props.history.push(ROUTES.IN_APP_TODAY);
  };

  fetchMoreQuickLinks = async () => {
    const { setMessage } = this.props;

    try {
      await this.data?.store?.fetchMoreQuickLinks();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    this.data?.store?.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && this.data?.store?.fetch();
  };

  onSearchClear = () => {
    this.data?.store?.clearSearch();
    this.data?.store?.fetch();
  };

  onAddQuickLinks = () => {
    const { openPopup } = this.props;

    openPopup({
      key: "AddQuickLinks",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <AddQuickLinksForm
          isLoading={this.data?.store?.isDetailsManaging}
          onSubmit={this.onAddQuickLinksSubmit(close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
          rootRoute={this.rootRoute}
        />
      ),
    });
  };

  onAddQuickLinksSubmit = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const { quickLinks } = values;

    const payload = {
      quickLinkIds: quickLinks?.map(({ id }: { id: number }) => id),
    };

    if (!payload.quickLinkIds || payload.quickLinkIds?.length === 0) {
      this.resetIsPopupDirty();
      close();
      return;
    }

    try {
      await this.data?.store?.addQuickLinkToCategory(payload);
      this.resetIsPopupDirty();
      close();
      this.data?.store?.resetPage();
      this.data?.store?.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onManageAccessPrograms = (quickLink: any) => () => {
    const { openPopup } = this.props;

    const shouldShowUpdateConfirmation = [
      PUBLISH_STATUSES.PUBLISHED,
      PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
    ].includes(quickLink.publishStatus);

    openPopup({
      key: "ManageQuickLink",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <AccessForm
          isLoading={!!this.data?.store?.isDetailsManaging}
          initialValues={{
            quickLinkId: quickLink.id,
            ...quickLink.accessFormData,
          }}
          onSubmit={this.onManageAccessProgramsSubmit(quickLink, close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
          shouldShowUpdateConfirmation={shouldShowUpdateConfirmation}
        />
      ),
    });
  };

  onManageAccessProgramsSubmit = (quickLink: any, close: () => void) => async (
    values: any,
  ) => {
    const { setMessage } = this.props;
    const { activePrograms } = values;

    const payload = {
      quickLinkId: quickLink.id,
      activePrograms: activePrograms.map(
        ({ id, switchState }: { id: number; switchState: boolean }) => ({
          activeProgramId: id,
          permission: switchState
            ? PROGRAM_PERMISSION_TYPES.PREVIEW
            : PROGRAM_PERMISSION_TYPES.FULL_ACCESS,
        }),
      ),
    };

    try {
      await this.data?.store?.updateQuickLinkAccessPrograms(payload);
      this.resetIsPopupDirty();
      close();
      this.data?.store?.resetPage();
      this.data?.store?.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onRemoveWorkout = (quickLink: any) => () => {
    const { openPopup } = this.props;

    const text = getConfirmRemovalText({
      entity: quickLink,
      suffix: "quick link",
      category: { title: "Today", publishStatus: PUBLISH_STATUSES.PUBLISHED },
    });

    openPopup({
      key: "RemoveQuickLink",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title="Remove Quick Link"
          text={text}
          declineText="Cancel"
          acceptText="Remove"
          onDecline={close}
          onAccept={this.onRemoveQuickLinkSubmit(close, quickLink)}
          isLoading={this.data?.store?.isDetailsManaging}
        />
      ),
    });
  };

  onRemoveQuickLinkSubmit = (close: () => void, quickLink: any) => async () => {
    const { setMessage } = this.props;

    const payload = { quickLinkIds: [quickLink?.id] };

    try {
      await this.data?.store?.removeQuickLinkFromCategory(payload);
      close();
      this.data?.store?.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onDetailsClick = (quickLink: any) => () => {
    if (quickLink.originalQuickLinkId) {
      const path = generatePath(this.data?.detailsRoute, {
        quickLinkId: quickLink.originalQuickLinkId,
      });
      this.props.history.push({ pathname: path, state: { readOnly: true } });
    }
  };

  onDragEnd = async (result: DropResult) => {
    const { setMessage } = this.props;

    // dropped outside the list
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index
    ) {
      return;
    }

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    try {
      await this.data?.store?.reorderQuickLinks({
        startIndex,
        endIndex,
      });
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Container header={<Header controls={<PublishButton />} />}>
        <Page
          topControls={
            <BackButtonWrapper>
              <Button
                color="secondary"
                variant="text"
                startIcon={<KeyboardBackspaceIcon />}
                onClick={this.onBackClick}
              >
                Back to Today
              </Button>
            </BackButtonWrapper>
          }
          title={this.data?.pageTitle}
          controls={
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={this.onAddQuickLinks}
            >
              Add Quick Link
            </Button>
          }
          isLoading={this.data?.store?.isLoading}
        >
          <SearchContainer>
            <Search
              initValue={this.data?.store?.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
          </SearchContainer>

          {!this.quickLinksListIsEmpty ? (
            <>
              {this.data?.store?.isQuickLinkReordering && <OverflowLoader />}
              <InfiniteScroll
                dataLength={this.quickLinks.length}
                hasMore={!!this.data?.store?.hasMoreInfo}
                next={this.fetchMoreQuickLinks}
                height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
                loader={<ScrollLoader />}
              >
                <DragDropContext onDragEnd={this.onDragEnd}>
                  <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {this.quickLinks.map((quickLink, index) => (
                          <Draggable
                            key={quickLink.id}
                            draggableId={quickLink.id.toString()}
                            index={index}
                            isDragDisabled={!this.isFiltersEmpty}
                          >
                            {(provided, snapshot) => (
                              <CategoryCardWrapper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <CategoryDetailsCard
                                  title={quickLink.title}
                                  image={quickLink.imageUrl}
                                  publishStatus={quickLink.publishStatus}
                                  onDetailsClick={this.onDetailsClick(
                                    quickLink,
                                  )}
                                  onAccessClick={this.onManageAccessPrograms(
                                    quickLink,
                                  )}
                                  onRemove={this.onRemoveWorkout(quickLink)}
                                  isDragging={snapshot.isDragging}
                                  dragHandleProps={provided.dragHandleProps}
                                />
                              </CategoryCardWrapper>
                            )}
                          </Draggable>
                        ))}

                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </InfiniteScroll>
            </>
          ) : (
            <Placeholder
              height={280}
              width={280}
              text={
                this.data?.store?.search
                  ? "No matches found. Please try another search."
                  : "No quick links added yet."
              }
            />
          )}
        </Page>
      </Container>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(TodayDetails)));
